import { useState, useEffect, useCallback, useMemo } from 'react'
import { UserProfileApi, UserContributionDto } from '@/shared/api/users'
import { useAuth } from '@/modules/auth'

export function useUserContributions(userId: string) {
  const { getAccessToken, isAuthenticated } = useAuth()
  const [contributions, setContributions] = useState<UserContributionDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create API instance
  const userProfileApi = useMemo(() => new UserProfileApi(getAccessToken), [getAccessToken])

  // Load contributions
  const loadContributions = useCallback(async () => {
    if (!userId || !isAuthenticated) {
      setContributions([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await userProfileApi.getUserContributions(userId)
      setContributions(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load contributions'
      setError(errorMessage)
      setContributions([])
    } finally {
      setIsLoading(false)
    }
  }, [userId, isAuthenticated, userProfileApi])

  // Load on mount and when userId changes
  useEffect(() => {
    loadContributions()
  }, [loadContributions])

  return {
    contributions,
    isLoading,
    error,
    loadContributions
  }
}

